/**
 * Create / edit an Area. Single modal handles both flows.
 */

import { useEffect, useState } from 'react';
import { useCreateArea, useUpdateArea, type AreaDto } from '../api/areas';
import { useContexts } from '../api/catalog';
import { FormModal } from './FormModal';
import styles from './FormModal.module.css';

interface AreaFormModalProps {
  open: boolean;
  onClose: () => void;
  area?: AreaDto | null;
}

const CADENCE_OPTIONS = ['Daily', 'Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Yearly'];

export function AreaFormModal({ open, onClose, area }: AreaFormModalProps) {
  const isEdit = !!area;
  const create = useCreateArea();
  const update = useUpdateArea();
  const contextsQ = useContexts();
  const contexts = contextsQ.data?.contexts ?? [];

  const [name, setName] = useState('');
  const [standard, setStandard] = useState('');
  const [cadence, setCadence] = useState<string | null>(null);
  const [contextId, setContextId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(area?.name ?? '');
    setStandard(area?.standard ?? '');
    setCadence(area?.cadence ?? null);
    setContextId(area?.contextId ?? null);
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, area?.id]);

  const pending = create.isPending || update.isPending;
  const valid = name.trim().length > 0 && standard.trim().length > 0;
  const error =
    (create.error as Error | null)?.message ??
    (update.error as Error | null)?.message ??
    null;

  const submit = () => {
    if (!valid || pending) return;
    const payload = {
      name: name.trim(),
      standard: standard.trim(),
      cadence: cadence ?? null,
      contextId,
    };
    if (isEdit && area) {
      update.mutate({ id: area.id, input: payload }, { onSuccess: () => onClose() });
    } else {
      create.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit area' : 'New area'}
      subtitle={
        isEdit
          ? undefined
          : 'An area is a standing responsibility. It defines what "kept up" looks like.'
      }
      submitLabel={isEdit ? 'Save changes' : 'Create area'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="area-name">Name</label>
        <input
          id="area-name"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Health, Casa, Boxfusion · Delivery"
          maxLength={120}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="area-standard">Standard</label>
        <textarea
          id="area-standard"
          className={styles.textarea}
          value={standard}
          onChange={(e) => setStandard(e.target.value)}
          placeholder="What does keeping this area healthy look like?"
          maxLength={500}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Cadence (optional)</span>
        <div className={styles.pills}>
          <button
            type="button"
            className={`${styles.pill} ${cadence === null ? styles.pillActive : ''}`}
            onClick={() => setCadence(null)}
          >
            None
          </button>
          {CADENCE_OPTIONS.map((c) => (
            <button
              type="button"
              key={c}
              className={`${styles.pill} ${cadence === c ? styles.pillActive : ''}`}
              onClick={() => setCadence(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Context</span>
        <div className={styles.pills}>
          <button
            type="button"
            className={`${styles.pill} ${contextId === null ? styles.pillActive : ''}`}
            onClick={() => setContextId(null)}
          >
            None
          </button>
          {contexts.map((ctx) => (
            <button
              type="button"
              key={ctx.id}
              className={`${styles.pill} ${contextId === ctx.id ? styles.pillActive : ''}`}
              onClick={() => setContextId(ctx.id)}
            >
              <span className={styles.pillDot} style={{ background: ctx.color }} />
              {ctx.label}
            </button>
          ))}
        </div>
      </div>
    </FormModal>
  );
}
